import { Link, /*useLocation,*/ } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { gradientBorder, gradientHover } from '../theme';
import { useState, useEffect, useRef } from 'react';
import { theme } from '../theme';
import { useHelpResources } from '../hooks/useHelpResources';

const GlobeIcon = ({ className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="url(#globeGradient)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <defs>
      <linearGradient id="globeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor={theme.colors.primary.start} />
        <stop offset="100%" stopColor={theme.colors.primary.end} />
      </linearGradient>
    </defs>
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const HelpCircleIcon = ({ className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="url(#helpGradient)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <defs>
      <linearGradient id="helpGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor={theme.colors.primary.start} />
        <stop offset="100%" stopColor={theme.colors.primary.end} />
      </linearGradient>
    </defs>
    <circle cx="12" cy="12" r="10" />
    <text x="12" y="17" textAnchor="middle" fill="url(#helpGradient)" fontSize="16" fontWeight="bold">?</text>
  </svg>
);

const InfoIcon = ({ className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="url(#infoGradient)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <defs>
      <linearGradient id="infoGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor={theme.colors.primary.start} />
        <stop offset="100%" stopColor={theme.colors.primary.end} />
      </linearGradient>
    </defs>
    <circle cx="12" cy="12" r="10" />
    <text x="12" y="17" textAnchor="middle" fill="url(#infoGradient)" fontSize="16">i</text>
  </svg>
);

function Header() {
  const { t, i18n } = useTranslation();
  // const location = useLocation();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const { 
    tipsLink,
    contactLink,
    donationLink,
    refreshResources,
    animationEnabled
  } = useHelpResources();
  
  const languageMenuRef = useRef<HTMLDivElement>(null);
  const helpMenuRef = useRef<HTMLDivElement>(null);
  const helpButtonRef = useRef<HTMLButtonElement>(null);

  const languages = {
    fr: 'Français',
    en: 'English'
  };

  // Vérifier si nous sommes sur la page d'accueil
  // const isHomePage = location.pathname === '/' || location.pathname === '/home';

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setShowLanguageMenu(false);
      }

      if (helpMenuRef.current && !helpMenuRef.current.contains(event.target as Node)) {
        setShowHelpMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = (lang: 'fr' | 'en') => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
    setShowLanguageMenu(false);
    
    // Force le rafraîchissement des composants qui dépendent de la langue
    window.dispatchEvent(new Event('languageChanged'));
  };

  const handleHelpButtonClick = () => {
    setShowHelpMenu(!showHelpMenu);
    refreshResources();
  };

  const handleTipsClick = () => {
    setShowHelpMenu(false);
    
    if (tipsLink) {
      console.log('Ouverture du lien conseils:', tipsLink);
      const link = document.createElement('a');
      link.href = tipsLink;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    } else {
      console.warn('Le lien des conseils n\'est pas disponible');
    }
  };

  const handleContactClick = () => {
    setShowHelpMenu(false);
    
    if (contactLink) {
      console.log('Ouverture du lien contact:', contactLink);
      const link = document.createElement('a');
      link.href = contactLink;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    } else {
      console.warn('Le lien de contact n\'est pas disponible');
    }
  };

  return (
    <header className="bg-black border-b border-primary-start/20">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="/Final Logo 192.png" 
            alt="MyCount" 
            className="w-10 h-10 object-contain -mt-1"
          />
          <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight opacity-75 -mt-1.5">MyCount</h1>
        </Link>
        
        <div className="flex items-center gap-4">
          <div className="relative" ref={helpMenuRef}>
            <button
              ref={helpButtonRef}
              onClick={handleHelpButtonClick}
              className={`relative hover:opacity-80 transition-all p-2 ${animationEnabled ? 'help-button-highlight' : ''}`}
              title={t('common.customerService')}
              aria-label="Service client"
            >
              <HelpCircleIcon className="w-6 h-6" />
            </button>

            {showHelpMenu && (
              <div className="fixed sm:absolute inset-x-0 sm:inset-x-auto sm:right-0 top-0 sm:top-full sm:mt-2 h-screen sm:h-auto bg-black sm:bg-[#0A0A0A] sm:border sm:border-primary-start/20 sm:rounded-2xl sm:shadow-lg overflow-hidden z-50 w-full sm:w-64">
                <div className="sticky top-0 bg-[#0A0A0A] border-b border-primary-start/20 p-4 sm:hidden">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white font-semibold text-lg">
                      <span className="bg-gradient-to-r from-primary-start to-primary-end bg-clip-text text-transparent">
                        {t('common.customerService')}
                      </span>
                    </h3>
                    <button
                      onClick={() => setShowHelpMenu(false)}
                      className="text-gray-400 hover:text-white"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>
                <div className="py-1 sm:py-1">
                  <button
                    onClick={handleTipsClick}
                    className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gradient-to-r hover:from-primary-start/5 hover:to-primary-end/5 transition-colors"
                  >
                    <InfoIcon className="w-5 h-5" />
                    <span className={`text-white text-sm ${animationEnabled ? 'tips-indicator' : ''}`}>
                      {t('profile.help.tips')}
                    </span>
                  </button>

                  <button
                    onClick={() => {
                      setShowHelpMenu(false);
                      if (donationLink) {
                        window.open(donationLink, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gradient-to-r hover:from-primary-start/5 hover:to-primary-end/5 transition-colors"
                  >
                    <div className="relative">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="url(#supportGradient)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <defs>
                          <linearGradient id="supportGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor={theme.colors.primary.start} />
                            <stop offset="100%" stopColor={theme.colors.primary.end} />
                          </linearGradient>
                        </defs>
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        <path d="M12 5.67C8.5 2.17 2 6.17 2 10.84c0 2.5 2 4.5 4 6.5s6 5 6 5" />
                        <path d="M12 5.67c3.5-3.5 10 .5 10 5.17 0 2.5-2 4.5-4 6.5s-6 5-6 5" />
                      </svg>
                    </div>
                    <span className="text-white text-sm">{t('profile.help.support')}</span>
                  </button>
                  
                  <button
                    onClick={handleContactClick}
                    className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gradient-to-r hover:from-primary-start/5 hover:to-primary-end/5 transition-colors"
                  >
                    <HelpCircleIcon className="w-5 h-5" />
                    <span className="text-white text-sm">{t('profile.help.contact')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={languageMenuRef}>
            <button
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              className="flex items-center justify-center w-8 h-8 hover:opacity-80 transition-opacity"
            >
              <GlobeIcon className="w-6 h-6" />
            </button>

            {showLanguageMenu && (
              <div className={`absolute right-0 mt-2 w-40 bg-[#0A0A0A] ${gradientBorder} rounded-xl shadow-lg overflow-hidden z-50`}>
                {(Object.entries(languages) as ['fr' | 'en', string][]).map(([code, name]) => (
                  <button
                    key={code}
                    onClick={() => handleLanguageChange(code)}
                    className={`w-full px-4 py-2 text-left ${gradientHover} transition-colors ${
                      i18n.language === code ? 'text-[#B8A44E]' : 'text-white'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;